import React, { useMemo, useState } from 'react';
import { Dropdown, FormOption } from 'common/form';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';
import { TemplateItem } from 'modules/analyse-templates/types/TemplateItem';
import { useGetAutomationTasksTemplateByOrganizationQuery } from 'generated-graphql/hooks/apollo-hooks';
import { useConfirmationModal } from 'services/modals/hooks';
import { splitAutomationItem } from 'core/utils/splitAutomationItem';
import { AutomationFormItem } from 'modules/analyse/types/AutomationFormItem';
import AddIcon from '@mui/icons-material/Add';
import { useDrawer } from 'services/drawer/hooks';
import { DrawerComponentType } from 'services/drawer/types';
import { useSetDefaultTemplate } from 'hooks/useSetDefaultTemplate';
import { Chip } from '@mui/material';

type Props = {
  onChange: ({
    rules,
    actions
  }: {
    templateId: string;
    rules: AutomationFormItem[];
    actions: AutomationFormItem[];
  }) => void;
  hideCreateButton?: boolean;
  autofillDefaultTemplate?: boolean;
  value?: string;
};

export const AnalyseTemplatesDropdown: React.FC<Props> = ({
  onChange,
  hideCreateButton,
  value,
  autofillDefaultTemplate = false
}) => {
  const organization = useOrganization();

  const [analyseTemplates, setAnalyseTemplates] = useState<TemplateItem[]>([]);

  const { showModal } = useConfirmationModal();
  const { openDrawer } = useDrawer();
  const { organizationConfig } = useSetDefaultTemplate();

  const { loading, refetch } = useGetAutomationTasksTemplateByOrganizationQuery(
    {
      variables: {
        organizationId: organization.id
      },
      onCompleted: (data) => {
        if (!data) {
          return [];
        }

        const result = extractQueryListItems(data);

        if (value && autofillDefaultTemplate) {
          handleChange(value, result as TemplateItem[]);
        }

        setAnalyseTemplates(result as TemplateItem[]);
      }
    }
  );
  const handleChange = (
    templateId: string,
    templatesOverride?: TemplateItem[]
  ) => {
    const templatesToSearch = templatesOverride || analyseTemplates;
    const chosenTemplate = templatesToSearch.find(
      (template) => template.id === templateId
    );

    const { rulesTasks, actionTasks } = splitAutomationItem(
      chosenTemplate?.automationTasks
    );

    onChange({
      rules: rulesTasks || [],
      actions: actionTasks || [],
      templateId
    });
  };

  const showModalBeforeChange = (
    templateId: string,
    templatesOverride?: TemplateItem[]
  ) => {
    showModal({
      title: 'Replace rules and actions?',
      message:
        'Choosing this template will overwrite any rules or actions you’ve added. This cannot be undone',
      confirmationText: 'Replace',
      onConfirm: () => handleChange(templateId, templatesOverride)
    });
  };

  const refetchAndSetTemplates = async () => {
    const result = await refetch();

    if (!result) {
      return;
    }

    const items = extractQueryListItems(result.data);

    setAnalyseTemplates(items as TemplateItem[]);
  };

  const setNewTemplateCreatedTemplate = async (id: string) => {
    const result = await refetch();

    if (!result) {
      return;
    }

    const items = extractQueryListItems(result.data) as TemplateItem[];
    setAnalyseTemplates(items);
    showModalBeforeChange(id, items);
  };

  const formattedAnalyseTemplateOptions = useMemo<FormOption[]>(() => {
    if (!analyseTemplates.length) {
      return [];
    }

    return analyseTemplates.map<FormOption>((template) => ({
      label: template.title,
      value: template.id,
      startComponents:
        template.id === organizationConfig?.defaultAutomationTaskTemplateId
          ? [
              <Chip
                key={template.id}
                label="Default"
                size="small"
                variant="inverse"
                color="primary"
              />
            ]
          : undefined
    }));
  }, [analyseTemplates, organizationConfig?.defaultAutomationTaskTemplateId]);

  const openTemplateCreationDrawer = () => {
    openDrawer({
      drawerContentType: DrawerComponentType.TemplateCreationDrawer,
      drawerComponentProps: {
        onTemplateCreated: (value: string) =>
          setNewTemplateCreatedTemplate(value),
        hideCloseIconButton: true
      }
    });
  };

  return (
    <Dropdown
      field={{
        buttonText: 'Choose',
        label: 'Use template (optional)',
        options: formattedAnalyseTemplateOptions,
        containerSx: { width: '480px' },
        isLoading: loading,
        onRefresh: refetchAndSetTemplates,
        stickyButtonEndComponent: hideCreateButton
          ? undefined
          : {
              text: 'Create new template',
              startIcon: <AddIcon />,
              onClick: openTemplateCreationDrawer
            }
      }}
      value={value}
      onChange={(templateId) => showModalBeforeChange(templateId)}
    />
  );
};
